import React, { ChangeEvent, useEffect, useState } from 'react'
import { styled } from '@mui/material/styles'
import {
  Button as MuiButton,
  Grid as MuiGrid,
  TextField as MuiTextField,
  CardHeader,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Divider as MuiDivider,
  IconButton,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Table,
  TableRow,
  Box,
  Stack,
  Tooltip
} from '@mui/material'
import * as Yup from 'yup'
import { spacing } from '@mui/system'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Formik, FormikHelpers } from 'formik'
import { RootState } from '../../redux'
import { getLevelsByProjectId } from '../../redux/slices/levels'
import { getLiftsByProjectId } from '../../redux/slices/lifts'
import {
  getPowerSuppliesByProjectId,
  setPowerSupplyName,
  updatePowerSupply
} from '../../redux/slices/powerSupplies'
import { setData, setStep } from '../../redux/slices/steps'
import { setDevice } from '../../redux/slices/devices'
import { QRData } from '../../enums/QRData'
import powerSupplyService from '../../services/powerSupplyService'
import { createErrorOrSuccessNotification } from '../../redux/slices/notifications'
import { NotificationType } from '../../enums/NotificationType'
import { deleteError, setError } from '../../redux/slices/errors'
import { PowerSupplyErrors } from '../../enums/PowerSupplyErrors'
import { useAuth } from '../../hooks'
import { createUserNotification } from '../../utils/createUserNotification'
import { ActionRequest, Device, PowerSupply, PowerSupplyInput, Project } from '../../types'
import { ProductType } from '../../enums/ProductType'
import { deleteDeviceLocationChangeRequest } from '../../redux/slices/requests'
import { updateRequestState } from '../../services/requestService'
import { RequestStatus } from '../../enums/RequestStatus'
import { getActionRequestsByProject } from '../../services/projectService'
import LoadingState from '../LoadingState'
import { RequesterType } from '../../enums/RequesterType'
import productService from '../../services/productService'
import NominalCurrent from '../../enums/NominalCurrent'
import { NominalCurrentSelect100And250 } from '../NominalCurrentSelect'
import InfoIcon from '@mui/icons-material/Info'
import { PowerSupplyUpdateAction } from '../../enums'
import { translateProductType } from '../../utils/product'
import { Add } from '@mui/icons-material'
import InputCablesAutocomplete from '../InputCablesAutocomplete'
import FuseAutocomplete from '../FuseAutocomplete'

/**
 * The styled TextField
 */
const TextField = styled(MuiTextField)<{ my?: number }>(spacing)

/**
 * The styled Button
 */
const Button = styled(MuiButton)(spacing)

/**
 * The styled Grid
 */
const Grid = styled(MuiGrid)(spacing)

/**
 * The styled Divider
 */
const Divider = styled(MuiDivider)(spacing)

/**
 * The properties
 */
interface PowerSupplyFormProps {
  device: Device
  project: Project
  onCancel?: () => void
}

type PowerSupplyFormValues = PowerSupplyInput & {
  id: number
  action: PowerSupplyUpdateAction
}

export default function PowerSupplyForm({ device, project, onCancel }: PowerSupplyFormProps) {
  /**
   * The dispatch function
   */
  const dispatch = useDispatch()

  /**
   * The translate function
   */
  const [t] = useTranslation('common')

  /**
   * The steps of installation process
   */
  const { step } = useSelector((state: RootState) => state.steps)
  /**
   * The levels of project
   */
  const { levels } = useSelector((state: RootState) => state.levels)
  /**
   * The lifts of project
   */
  const { lifts } = useSelector((state: RootState) => state.lifts)
  /**
   * The powerSupplies
   */
  const { powerSupplies, powerSupplyName } = useSelector((state: RootState) => state.powerSupplies)

  /**
   * Is device type allowed to change fuse size
   */
  const [useFuseValidation, setUseFuseValidation] = useState<boolean>(false)

  /**
   * The validation schema
   */
  const validationSchema = useFuseValidation
    ? Yup.object().shape({
        name: Yup.string().required(
          t('isRequired', {
            field: t('name')
          })
        ),
        level: Yup.number().nullable(),
        lift: Yup.number().nullable(),
        inputCables: Yup.number().required(
          t('isRequired', {
            field: t('inputCables').toLowerCase()
          })
        ),
        fuse: Yup.number()
          .positive()
          .required(t('isRequired', { field: t('fuse') }))
      })
    : Yup.object().shape({
        name: Yup.string().required(
          t('isRequired', {
            field: t('name')
          })
        ),
        level: Yup.number().nullable(),
        lift: Yup.number().nullable(),
        inputCables: Yup.number().required(
          t('isRequired', {
            field: t('inputCables').toLowerCase()
          })
        )
      })

  /**
   * The device's requestts
   */
  const [deviceRequests, setDeviceRequests] = useState<ActionRequest[]>()

  /**
   * Is loading requests
   */
  const [isLoadingRequests, setIsLoadingRequests] = useState<boolean>(false)

  // TODO handle not loaded power-supply
  if (device.powerSupply === null || device.powerSupply === undefined) {
    return <></>
  }

  /**
   * Initial values
   */
  const initialValues: PowerSupplyFormValues = {
    ...device.powerSupply,
    // Reset the name.
    name: '',
    level: device.asset?.levelId ? device.asset.levelId : null,
    lift: device?.asset?.liftId ?? null,
    parent: device?.powerSupply?.parentId,
    isMainDistributionAssembly: device?.powerSupply?.isMainDistributionAssembly ?? false,
    fuse: device?.powerSupply?.fuse ?? 0,
    inputCables: device?.powerSupply?.inputCables ?? 1,
    project: device?.asset?.project ? device?.asset?.project.id : project.id,
    voltage_1_tp: device?.powerSupply?.voltage_1_tp ?? 0,
    voltage_1_ts: device?.powerSupply?.voltage_1_ts ?? 0,
    action: PowerSupplyUpdateAction.INSTALL,
    description: null
  }

  useEffect(() => {
    dispatch(getLevelsByProjectId(project.id))
    dispatch(getLiftsByProjectId(project.id))
    dispatch(getPowerSuppliesByProjectId(project.id))
    if (device?.powerSupply) {
      dispatch(setPowerSupplyName(device?.powerSupply?.name))
    }
    if (device.asset?.project) {
      loadRequests()
    }

    if (
      (device.product && device.product.type === ProductType.PLUG_AND_PLAY) ||
      (device.product && device.product.type === ProductType.INSTALLATION_KIT) ||
      (device.product && device.product.type === ProductType.SPINE_MODULE)
    ) {
      setUseFuseValidation(true)
    }
  }, [])

  async function loadRequests() {
    try {
      if (device?.asset?.project?.id === undefined) {
        return
      }
      setIsLoadingRequests(true)
      setDeviceRequests(
        await getActionRequestsByProject(device?.asset?.project?.id, {
          requesterType: RequesterType.DEVICE,
          requesterId: device?.name
        })
      )
    } finally {
      setIsLoadingRequests(false)
    }
  }

  /**
   * Main powersupplies
   */
  const mainPowerSupplies = powerSupplies.filter(
    (powerSupply: PowerSupply) =>
      powerSupply.isMainDistributionAssembly && powerSupply.id !== device?.powerSupply?.id
  )

  /**
   * The current user
   */
  const { currentUser } = useAuth()

  /**
   * The nominal current
   */
  const [nominalCurrent, setNominalCurrent] = useState(
    device?.powerSupply?.voltage_1_tp === 1000
      ? (NominalCurrent.NOMINAL100 as string)
      : device?.powerSupply?.voltage_1_tp === 2500
        ? (NominalCurrent.NOMINAL250 as string)
        : device?.powerSupply?.voltage_1_tp === 4000
          ? (NominalCurrent.NOMINAL400 as string)
          : device?.powerSupply?.voltage_1_tp === 6000
            ? (NominalCurrent.NOMINAL600 as string)
            : (NominalCurrent.OTHER as string)
  )

  /**
   * Open currentTransformer info popup
   */
  const [openCurrentTransformerInfo, setOpenCurrentTransformerInfo] = useState(false)

  const handleSubmit = async (
    values: PowerSupplyFormValues,
    { resetForm, setSubmitting }: FormikHelpers<PowerSupplyFormValues>
  ) => {
    try {
      /*Make parent id null if user marks current powersupply as main distribution assembly.
      This check is made to prevent error situation where user accidentally selects parent for powersupply before checking
      this powersupply as main distribution assembly.*/
      if (values.isMainDistributionAssembly === true && values.parent !== null) {
        if (values.level === null) {
          values.level = null
          values.lift = null
        }
        values.parent = null

        const updatedPowerSupply: PowerSupply = await powerSupplyService.updatePowerSupply(
          values.id,
          values
        )
        dispatch(updatePowerSupply(updatedPowerSupply))
        dispatch(setPowerSupplyName(''))
        dispatch(
          createErrorOrSuccessNotification(NotificationType.SUCCESS, t('powerSupplyUpdateSuccess'))
        )
      } else {
        if (values.level === null) {
          values.level = null
          values.lift = null
        }
        const updatedPowerSupply: PowerSupply = await powerSupplyService.updatePowerSupply(
          values.id,
          values
        )
        dispatch(updatePowerSupply(updatedPowerSupply))
        dispatch(setPowerSupplyName(''))

        dispatch(
          createErrorOrSuccessNotification(NotificationType.SUCCESS, t('powerSupplyUpdateSuccess'))
        )
      }
      if (device.product?.type === ProductType.INSTALLATION_KIT) {
        //Update product type from installation kit to smart electrical cabinet
        await productService.updateProduct(device.product.id, {
          type: ProductType.SMART_ELECTRICAL_CABINET
        })
      }

      if (deviceRequests && deviceRequests.length > 0) {
        await handleRequestsStatusUpdates(RequestStatus.APPROVED)
      }
      dispatch(setStep(step + 3))
      dispatch(setDevice(undefined))
      dispatch(setData(QRData.NOT_SCANNED))
      resetForm()
    } catch (error: unknown) {
      dispatch(
        setError({
          type: PowerSupplyErrors.EDIT,
          error: error
        })
      )

      //Create message
      const errorMessage = createUserNotification({
        user: currentUser,
        type: PowerSupplyErrors.EDIT,
        error: error
      })

      //Dispatch error message
      dispatch(
        createErrorOrSuccessNotification(
          NotificationType.WARNING,
          t(errorMessage.key) + t(errorMessage.message)
        )
      )
      //TODO: Do not delete errors until submitting form has passed if error is validation error from the backend
      dispatch(deleteError(PowerSupplyErrors.EDIT))
    } finally {
      setSubmitting(false)
    }
  }

  const handleCancel = () => {
    onCancel?.()
  }

  async function handleRequestsStatusUpdates(status: RequestStatus): Promise<void> {
    if (deviceRequests !== undefined) {
      await Promise.all(
        deviceRequests?.map(async (request: ActionRequest) => {
          const updatedRequest = await updateRequestState(request.id, {
            status: status,
            approver: currentUser?.firstName + ' ' + currentUser?.lastName
          })
          dispatch(deleteDeviceLocationChangeRequest(updatedRequest.id))
        })
      )
    }
  }

  return (
    <React.Fragment>
      {/**The currentTransformer info popup */}
      <Dialog
        open={openCurrentTransformerInfo}
        onClose={() => setOpenCurrentTransformerInfo(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{t('currentTransformerInfoTitle')}</DialogTitle>

        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            <Table>
              <TableRow>
                {t('currentTransformer100A') + ': ' + 'SCT016 100 A : 0,1 A (100 mA), 1%'}
              </TableRow>
              <TableRow>
                {t('currentTransformer250A') + ': ' + 'SCT024 250 A : 0,1 A (100 mA), 1%'}
              </TableRow>
              <TableRow>{t('other') + ': ' + t('currentTransformerOtherInfo')}</TableRow>
            </Table>
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button
            variant="contained"
            onClick={() => setOpenCurrentTransformerInfo(false)}
            autoFocus
          >
            {t('understood')}
          </Button>
        </DialogActions>
      </Dialog>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ errors, handleBlur, handleChange, handleSubmit, touched, values, setValues }) => (
          <>
            {isLoadingRequests ? (
              <LoadingState />
            ) : (
              <form onSubmit={handleSubmit}>
                <CardHeader
                  title={t('Manufacturing.instructionsForPowerSupply')}
                  align="center"
                  titleTypographyProps={{ variant: 'h6' }}
                />

                {(device?.product?.type === ProductType.INSTALLATION_KIT ||
                  device?.product?.type === ProductType.PLUG_AND_PLAY) && (
                  <Alert severity="warning">
                    {`${t('installationTypeNotification')} ${translateProductType(
                      device.product.type
                    )}`}
                  </Alert>
                )}

                <Divider sx={{ my: 6 }} />

                <Grid container spacing={6}>
                  <Grid item xs={12}>
                    <TextField
                      type="text"
                      name="name"
                      label={t('Manufacturing.name')}
                      value={values.name}
                      error={Boolean(touched.name && errors.name)}
                      helperText={touched.name && errors.name}
                      fullWidth
                      onBlur={handleBlur}
                      onChange={handleChange}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Grid item container spacing={6}>
                      <Grid item xs={12}>
                        <Alert severity="info">{t('areaHelperText')}</Alert>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <TextField
                          select
                          name="level"
                          label={t('Manufacturing.level')}
                          value={values.level}
                          error={Boolean(touched.level && errors.level)}
                          helperText={touched.level && errors.level}
                          fullWidth
                          onBlur={handleBlur}
                          onChange={handleChange}
                        >
                          {levels.map((level) => (
                            <MenuItem key={level.id} value={level.id}>
                              {level.name}
                            </MenuItem>
                          ))}
                          <Divider />
                          <Box sx={{ px: 4, pt: 1 }}>
                            <Button
                              variant="text"
                              startIcon={<Add fontSize="inherit" />}
                              size="small"
                              onClick={() => dispatch(setStep(step + 1))}
                            >
                              {t('newLevel')}
                            </Button>
                          </Box>
                        </TextField>

                        {values.level !== null && (
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Button
                              variant="text"
                              size="small"
                              onClick={() => {
                                setValues({
                                  ...values,
                                  level: null,
                                  lift: null
                                })
                              }}
                            >
                              {t('clearSelection')}
                            </Button>
                          </Box>
                        )}
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Grid container spacing={6}>
                          <Grid item xs={8} sm={8} md={8} lg={8} xl>
                            <TextField
                              select
                              name="lift"
                              label={t('Manufacturing.lift')}
                              disabled={values.level === null}
                              value={values.lift}
                              error={Boolean(touched.lift && errors.lift)}
                              helperText={touched.lift && errors.lift}
                              fullWidth
                              onBlur={handleBlur}
                              onChange={handleChange}
                            >
                              {lifts.map((lift) => (
                                <MenuItem key={lift.id} value={lift.id}>
                                  {lift.name}
                                </MenuItem>
                              ))}
                              <Divider />
                              <Box sx={{ px: 4, pt: 1 }}>
                                <Button
                                  variant="text"
                                  startIcon={<Add fontSize="inherit" />}
                                  size="small"
                                  onClick={() => dispatch(setStep(step + 2))}
                                >
                                  {t('newLift')}
                                </Button>
                              </Box>
                            </TextField>

                            {values.lift !== null && (
                              <Box
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'flex-end'
                                }}
                              >
                                <Button
                                  variant="text"
                                  size="small"
                                  onClick={() => {
                                    setValues({
                                      ...values,
                                      lift: null
                                    })
                                  }}
                                >
                                  {t('clearSelection')}
                                </Button>
                              </Box>
                            )}
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>

                  {useFuseValidation && (
                    <Grid item xs={12} md={6}>
                      <FuseAutocomplete
                        value={values.fuse}
                        error={Boolean(touched.fuse && errors.fuse)}
                        helperText={touched.fuse && errors.fuse}
                        fullWidth
                        onBlur={handleBlur}
                        onChange={(fuse) => {
                          setValues({
                            ...values,
                            fuse
                          })
                        }}
                      />

                      {/*
                      <PowerSupplyFuseSelect
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <IconButton
                                sx={{ p: '10px' }}
                                aria-label="menu"
                                onClick={handleClickShowFuseSelect}
                              >
                                {showFuseSelect ? (
                                  <TextFieldsIcon />
                                ) : (
                                  <MenuIcon />
                                )}
                              </IconButton>
                            </InputAdornment>
                          ),
                          endAdornment:
                            showFuseSelect === false ? (
                              <InputAdornment
                                position="end"
                                style={{ color: 'white' }}
                              >
                                A
                              </InputAdornment>
                            ) : (
                              ''
                            )
                        }}
                        select={showFuseSelect}
                        value={values.fuse}
                        error={Boolean(touched.fuse && errors.fuse)}
                        helperText={touched.fuse && errors.fuse}
                        fullWidth
                        onBlur={handleBlur}
                        onChange={handleChange}
                      />
                      */}
                    </Grid>
                  )}

                  {device.product?.type !== ProductType.SPINE_MODULE && (
                    <Grid item xs={12} md={6}>
                      <InputCablesAutocomplete
                        value={values.inputCables}
                        error={Boolean(touched.inputCables && errors.inputCables)}
                        helperText={touched.inputCables && errors.inputCables}
                        fullWidth
                        onBlur={handleBlur}
                        onChange={(inputCables) => {
                          setValues({
                            ...values,
                            inputCables
                          })
                        }}
                      />

                      {/*<PowerSupplyInputCablesSelect
                        value={values.inputCables}
                        error={Boolean(
                          touched.inputCables && errors.inputCables
                        )}
                        helperText={touched.inputCables && errors.inputCables}
                        fullWidth
                        onBlur={handleBlur}
                        onChange={handleChange}
                      />*/}
                    </Grid>
                  )}

                  {device?.product?.type === ProductType.INSTALLATION_KIT && (
                    /**
                     * Nominalcurrent select for installationKit
                     */
                    <Grid item xs={12}>
                      <Grid container spacing={6}>
                        <Grid item xs={4}>
                          <Stack direction="row" alignItems="center" gap={3}>
                            <Tooltip title={t('info')}>
                              <IconButton
                                size="small"
                                onClick={() => setOpenCurrentTransformerInfo(true)}
                              >
                                <InfoIcon />
                              </IconButton>
                            </Tooltip>
                            <NominalCurrentSelect100And250
                              name="nominalCurrent"
                              value={nominalCurrent}
                              fullWidth
                              onBlur={handleBlur}
                              onChange={(e) => setNominalCurrent(e.target.value)}
                            />
                          </Stack>
                        </Grid>

                        <Grid item xs={12} md={4}>
                          <TextField
                            name="voltage_1_tp"
                            sx={{
                              display: nominalCurrent === NominalCurrent.OTHER ? 'default' : 'none'
                            }}
                            label={t('PowerSupply.voltage_1_tp')}
                            value={
                              nominalCurrent === NominalCurrent.NOMINAL100
                                ? (values.voltage_1_tp = 1000)
                                : nominalCurrent === NominalCurrent.NOMINAL250
                                  ? (values.voltage_1_tp = 2500)
                                  : values.voltage_1_tp
                            }
                            fullWidth
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                        </Grid>

                        <Grid item xs={12} md={4}>
                          <TextField
                            name="voltage_1_ts"
                            sx={{
                              display: nominalCurrent === NominalCurrent.OTHER ? 'default' : 'none'
                            }}
                            label={t('PowerSupply.voltage_1_ts')}
                            value={
                              nominalCurrent === NominalCurrent.NOMINAL100
                                ? (values.voltage_1_ts = 1)
                                : nominalCurrent === NominalCurrent.NOMINAL250
                                  ? (values.voltage_1_ts = 1)
                                  : values.voltage_1_ts
                            }
                            fullWidth
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <FormControlLabel
                          label={t('PowerSupply.isMainDistributionAssembly')}
                          control={
                            <Checkbox
                              name="isMainDistributionAssembly"
                              checked={values.isMainDistributionAssembly}
                              onChange={(event: ChangeEvent) => {
                                setValues({
                                  ...values,
                                  isMainDistributionAssembly:
                                    // @ts-expect-error typed incorrectly
                                    event.target.checked,
                                  parent: null
                                })
                              }}
                            />
                          }
                        />
                      </Grid>

                      {values.isMainDistributionAssembly === false && (
                        <Grid item xs={12}>
                          <TextField
                            select
                            name="parent"
                            label={t('Manufacturing.parentName')}
                            value={values.parent}
                            error={Boolean(touched.parent && errors.parent)}
                            helperText={touched.parent && errors.parent}
                            onBlur={handleBlur}
                            onChange={handleChange}
                            fullWidth
                          >
                            {mainPowerSupplies.map((powerSupply) => (
                              <MenuItem key={powerSupply.id} value={powerSupply.id}>
                                {powerSupply.name}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                      )}
                    </Grid>
                  </Grid>

                  <Grid item xs={12}>
                    <Stack direction="row" alignItems="center" justifyContent="flex-end" gap={3}>
                      <Button variant="text" color="inherit" onClick={handleCancel}>
                        {t('cancel')}
                      </Button>
                      <Button type="submit" variant="contained" color="primary">
                        {t('save')}
                      </Button>
                    </Stack>
                  </Grid>
                </Grid>
              </form>
            )}
          </>
        )}
      </Formik>
    </React.Fragment>
  )
}
